import { ModuleLesson, ModuleLessonTag } from '@yandex-int/k-common';
import b from 'b_';
import { ChevroneKit as Chevrone } from 'education-icons';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { Button, Text } from 'platform-components';
import { TooltipStateful } from 'platform-components/src/components/LegoTooltip/LegoTooltip';
import qs from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { SEARCH_SCROLL_TO_LESSON } from 'common.components/constants';

import { CourseContainerLesson } from '../__lesson/course-container__lesson';
import { actions } from '../actions';
import { getExpandedLessonGroup } from '../selectors';

import './course-container__lesson-group.scss';

interface Props {
    id: string;
    name: string;
    lessons: ModuleLesson[];
}

export const CourseContainerLessonGroup = ({ id, name, lessons }: Props) => {
    const dispatch = useDispatch();

    const tags = useMemo(() => {
        const allTags = flatten(lessons.map((lesson) => lesson.tags).filter((t): t is ModuleLessonTag[] => !isNil(t)));
        return uniqBy(allTags, (tag) => tag.title);
    }, [lessons]);

    useExpandLessonGroupFromSearchParams(id, lessons);

    const expandedLessonGroup = useSelector(getExpandedLessonGroup);

    const isExpanded = expandedLessonGroup === id;

    const handleClick = () => {
        dispatch(actions.setExpandedLessonGroupId({ lessonGroupId: isExpanded ? null : id }));
    };

    return (
        <div className="course-container__lesson-group" key={name}>
            <div className="course-container__lesson-group-header" {...accessibleClick(handleClick)}>
                <div className="course-container__lesson-group-title">
                    <div className="course-container__lesson-group-name">
                        <Text size={Text.SIZE.XL} type={Text.TYPE.MARKDOWN}>
                            {name}
                        </Text>
                    </div>
                    {tags.map((tag) => (
                        <TooltipStateful
                            className="course-container__lesson-group-tooltip"
                            content={tag.description}
                            direction="top"
                            hasTail
                            key={tag.title}
                            mainOffset={4}
                            trigger={tag.description ? 'hover' : []}
                            view="default"
                        >
                            <div className="course-container__lesson-group-tag" style={{ backgroundColor: tag.color }}>
                                <Text size={Text.SIZE.M} weight={Text.WEIGHT.M}>
                                    {tag.title}
                                </Text>
                            </div>
                        </TooltipStateful>
                    ))}
                </div>
                <Button
                    className={b('course-container', 'lesson-group-button', { expanded: isExpanded })}
                    icon={<Chevrone />}
                    onClick={handleClick}
                    rounded
                    size={Button.SIZE.L}
                    view={Button.VIEW.SECONDARY}
                />
            </div>
            {isExpanded && (
                <div className="course-container__lesson-group-content">
                    {lessons.map((lesson: ModuleLesson) => (
                        <CourseContainerLesson key={lesson.lesson.id} lesson={lesson} />
                    ))}
                </div>
            )}
        </div>
    );
};

function useExpandLessonGroupFromSearchParams(id: string | null, lessons: ModuleLesson[]) {
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const lessonsIds = useMemo(() => new Set(lessons.map((l) => l.lesson.id)), [lessons]);

    useEffect(() => {
        const params = qs.parse(search);
        const searchLessonId = Number(params[SEARCH_SCROLL_TO_LESSON]);

        if (!isNaN(searchLessonId) && lessonsIds.has(searchLessonId) && id) {
            dispatch(actions.setExpandedLessonGroupId({ lessonGroupId: id }));
        }
    }, [search, history, lessons, lessonsIds, dispatch, id]);
}
